import React, { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Calendar,
  MapPin,
  Link as LinkIcon,
  Video,
  ExternalLink,
  Download,
  PlayCircle,
  Share2,
  Bell,
  Star
} from "lucide-react";
import { motion } from "framer-motion";
import type { EventRow } from "../types/event";
import { normalizeDate, parseTags, pushGA, shareLinks, toGoogleCalendarUrl, formatInZoneISO } from "../lib/utils";
import { toICS } from "../lib/ics";
import Badge from "./Badge";
import ReminderModal from "./ReminderModal";
import RSVPBlock from "./RSVPBlock";

interface EventCardProps {
  ev: EventRow;
  past: boolean;
  viewMode?: 'grid' | 'list';
}

function DatePill({ iso }: { iso: string }) {
  const d = normalizeDate(iso);
  if (!d) return null;
  return (
    <div className="absolute left-4 top-4 z-10 rounded-xl bg-darling-lilac/95 dark:bg-deep-purple/95 backdrop-blur px-3 py-2 shadow-lg border border-darling-lilac/30 dark:border-deep-purple/50">
      <div className="text-[10px] uppercase tracking-wider text-white/80 dark:text-white/70 font-exo font-medium">
        {format(d, "EEE", { locale: ptBR })}
      </div>
      <div className="text-xl font-exo font-black leading-none text-white drop-shadow-sm">
        {format(d, "dd")}
      </div>
      <div className="text-xs text-white/90 dark:text-white/80 font-exo font-medium">
        {format(d, "MMM", { locale: ptBR })}
      </div>
    </div>
  );
}

function useInViewOnce<T extends HTMLElement>(): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (seen) return;
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !seen) {
          setSeen(true);
        }
      });
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [seen]);
  return [ref, seen];
}

export default function EventCard({ ev, past, viewMode = 'grid' }: EventCardProps) {
  const [showReminderModal, setShowReminderModal] = useState(false);
  
  const isHighlight = (ev.destaque || "").toLowerCase() === "sim";
  
  const when = ev.start_iso ? (
    <div className="text-sm text-soft-black/80 dark:text-light-gray flex items-center gap-2 font-exo">
      <Calendar className="h-4 w-4"/>
      <span>{formatInZoneISO(ev.start_iso)}</span>
    </div>
  ) : null;

  const tags = parseTags(ev.tags);
  const [ref, seen] = useInViewOnce<HTMLDivElement>();
  useEffect(() => {
    if (seen) pushGA('view_event', { event_id: ev.id, title: ev.title, start: ev.start_iso });
  }, [seen]);

  const handleICS = () => {
    const ics = toICS(ev);
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(ev.title || "evento").toLowerCase().replace(/[^a-z0-9]+/gi, "-")}.ics`;
    a.click();
    URL.revokeObjectURL(url);
    pushGA('download_ics', { event_id: ev.id });
  };

  const share = shareLinks(ev);

  if (viewMode === 'list') {
    return (
      <motion.article 
        ref={ref} 
        initial={{ opacity: 0, y: 20 }} 
        whileInView={{ opacity: 1, y: 0 }} 
        viewport={{ once: true }} 
        transition={{ duration: 0.4 }}
        className="group rounded-2xl border border-light-gray-alt dark:border-deep-purple/50 bg-white dark:bg-soft-black shadow-lg hover:shadow-xl hover:shadow-darling-lilac/10 dark:hover:shadow-darling-lilac/20 transition-all duration-300 overflow-hidden"
      >
        <div className="flex flex-col md:flex-row">
          {/* Image/Date Section */}
          <div className="relative md:w-64 flex-shrink-0">
            <DatePill iso={ev.start_iso} />
            {/* Highlight Badge */}
            {isHighlight && (
              <div className="absolute top-4 right-4 z-10">
                <Badge className="bg-sun-flower/20 text-sun-flower border-sun-flower/30 dark:bg-sun-flower/10 dark:text-sun-flower dark:border-sun-flower/20">
                  <Star className="h-3 w-3 fill-current" />
                  Destaque
                </Badge>
              </div>
            )}
            {ev.cover_image ? (
              <div className="aspect-[16/9] md:aspect-square w-full bg-light-gray dark:bg-deep-purple/30 overflow-hidden">
                <img 
                  src={ev.cover_image} 
                  alt="capa do evento" 
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
            ) : (
              <div className="aspect-[16/9] md:aspect-square w-full grid place-items-center bg-gradient-to-br from-light-gray to-light-gray-alt dark:from-deep-purple/20 dark:to-soft-black/50">
                <PlayCircle className="h-12 w-12 text-darling-lilac/60"/>
              </div>
            )}
          </div>
          
          {/* Content Section */}
          <div className="flex-1 p-6 flex flex-col gap-4">
            <div className="flex-1">
              <div className="flex flex-col gap-3 mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-exo font-bold leading-tight text-soft-black dark:text-white group-hover:text-darling-lilac dark:group-hover:text-darling-lilac transition-colors mb-2">
                    {ev.title}
                  </h3>
                  {ev.series && (
                    <div className="text-sm text-darling-lilac dark:text-darling-lilac font-exo font-medium mb-3">
                      {ev.series} {ev.episode ? `– ${ev.episode}` : ''}
                    </div>
                  )}
                </div>
                
                <div className="flex flex-wrap gap-2 mb-3">
                  {tags.slice(0, 2).map((t) => (
                    <Badge key={t} className="bg-darling-lilac/10 text-darling-lilac border-darling-lilac/20 dark:bg-darling-lilac/20 dark:text-darling-lilac/90 dark:border-darling-lilac/30 font-exo">
                      #{t}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  {when}
                  {ev.location_name || ev.address ? (
                    <div className="text-sm text-soft-black/80 dark:text-light-gray/90 flex items-center gap-2 font-exo">
                      <MapPin className="h-4 w-4"/>
                      {ev.location_name || ev.address}
                    </div>
                  ) : null}
                  {ev.speakers ? (
                    <p className="text-sm text-soft-black/70 dark:text-light-gray/95 font-exo">
                      <span className="font-medium text-darling-lilac">Com:</span> {ev.speakers}
                    </p>
                  ) : null}
                </div>
                
                <div>
                  {ev.description ? (
                    <p className="text-sm text-soft-black/80 dark:text-light-gray/90 line-clamp-3 leading-relaxed font-exo font-light">
                      {ev.description}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-4 border-t border-light-gray-alt dark:border-deep-purple/30 pt-4">
              {/* RSVP Block para eventos futuros */}
              {!past && (
                <RSVPBlock 
                  event={ev} 
                  onShowReminder={() => {
                    setShowReminderModal(true);
                    pushGA('reminder_open', { event_id: ev.id });
                  }} 
                />
              )}
              
              <div className="flex flex-wrap gap-3">
                {!past ? (
                  <>
                    {ev.join_url && (
                      <a 
                        href={ev.join_url} 
                        target="_blank" 
                        rel="noreferrer" 
                        onClick={() => pushGA('click_join', { event_id: ev.id })} 
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-darling-lilac/30 bg-darling-lilac text-white hover:bg-deep-purple dark:border-darling-lilac dark:bg-darling-lilac dark:hover:bg-deep-purple px-4 py-2.5 text-sm font-exo font-semibold transition-colors shadow-sm"
                      >
                        <Video className="h-4 w-4"/> Entrar
                      </a>
                    )}
                    <a 
                      href={toGoogleCalendarUrl(ev)} 
                      target="_blank" 
                      rel="noreferrer" 
                      onClick={() => pushGA('add_to_calendar', { event_id: ev.id })} 
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-light-gray-alt dark:border-deep-purple/50 bg-light-gray dark:bg-deep-purple/30 hover:bg-light-gray-alt dark:hover:bg-deep-purple/50 px-4 py-2.5 text-sm font-exo font-medium text-soft-black dark:text-white transition-colors"
                    >
                      <Calendar className="h-4 w-4"/> Google Agenda
                    </a>
                    <button 
                      onClick={handleICS} 
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-light-gray-alt dark:border-deep-purple/50 bg-light-gray dark:bg-deep-purple/30 hover:bg-light-gray-alt dark:hover:bg-deep-purple/50 px-4 py-2.5 text-sm font-exo font-medium text-soft-black dark:text-white transition-colors"
                    >
                      <Download className="h-4 w-4"/> .ics
                    </button>
                  </>
                ) : (
                  <>
                    {ev.recording_url ? (
                      <a 
                        href={ev.recording_url} 
                        target="_blank" 
                        rel="noreferrer" 
                        onClick={() => pushGA('click_recording', { event_id: ev.id })} 
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-ocean-water/30 bg-ocean-water text-white hover:bg-teal-green dark:border-ocean-water dark:bg-ocean-water dark:hover:bg-teal-green px-4 py-2.5 text-sm font-exo font-semibold transition-colors shadow-sm"
                      >
                        <Video className="h-4 w-4"/> Gravação
                      </a>
                    ) : (
                      <span className="text-sm text-darling-lilac/60 dark:text-darling-lilac/70 py-2 font-exo">
                        Sem link de gravação informado.
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Reminder Modal */}
        <ReminderModal 
          isOpen={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          event={ev}
        />
      </motion.article>
    );
  }

  return (
    <motion.article 
      ref={ref} 
      initial={{ opacity: 0, y: 20 }} 
      whileInView={{ opacity: 1, y: 0 }} 
      viewport={{ once: true }} 
      transition={{ duration: 0.4 }}
      className="group rounded-2xl border border-light-gray-alt dark:border-deep-purple/50 bg-white dark:bg-soft-black shadow-lg hover:shadow-xl hover:shadow-darling-lilac/10 dark:hover:shadow-darling-lilac/20 transition-all duration-300 overflow-hidden flex flex-col"
    >
      <div className="relative">
        <DatePill iso={ev.start_iso} />
        {/* Highlight Badge */}
        {isHighlight && (
          <div className="absolute top-4 right-4 z-10">
            <Badge className="bg-sun-flower/20 text-sun-flower border-sun-flower/30 dark:bg-sun-flower/10 dark:text-sun-flower dark:border-sun-flower/20">
              <Star className="h-3 w-3 fill-current" />
              Destaque
            </Badge>
          </div>
        )}
        {ev.cover_image ? (
          <div className="aspect-[16/9] w-full bg-light-gray dark:bg-deep-purple/30 overflow-hidden">
            <img 
              src={ev.cover_image} 
              alt="capa do evento" 
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </div>
        ) : (
          <div className="aspect-[16/9] w-full grid place-items-center bg-gradient-to-br from-light-gray to-light-gray-alt dark:from-deep-purple/20 dark:to-soft-black/50">
            <PlayCircle className="h-12 w-12 text-darling-lilac/60"/>
          </div>
        )}
      </div>
      
      <div className="p-6 flex flex-col gap-4 flex-1">
        <div>
          <h3 className="text-xl font-exo font-bold leading-tight text-soft-black dark:text-white group-hover:text-darling-lilac dark:group-hover:text-darling-lilac transition-colors">
            {ev.title}
          </h3>
          {ev.series && (
            <div className="mt-1 text-sm text-darling-lilac/70 dark:text-darling-lilac/80 font-exo font-medium">
              {ev.series} {ev.episode ? `– ${ev.episode}` : ''}
            </div>
          )}
        </div>

        {when}
        
        {ev.location_name || ev.address ? (
          <div className="text-sm text-soft-black/80 dark:text-light-gray flex items-center gap-2 font-exo">
            <MapPin className="h-4 w-4"/>
            {ev.location_name || ev.address}
          </div>
        ) : null}
        
        {ev.speakers ? (
          <p className="text-sm text-soft-black/70 dark:text-light-gray/95 font-exo">
            <span className="font-medium text-darling-lilac">Com:</span> {ev.speakers}
          </p>
        ) : null}
        
        {ev.description ? (
          <p className="text-sm text-soft-black/80 dark:text-light-gray/90 line-clamp-3 leading-relaxed font-exo font-light">
            {ev.description}
          </p>
        ) : null}

        <div className="flex flex-wrap gap-2">
          {tags.map((t) => (
            <Badge key={t} className="bg-darling-lilac/10 text-darling-lilac border-darling-lilac/20 dark:bg-darling-lilac/20 dark:text-darling-lilac/90 dark:border-darling-lilac/30 font-exo">
              #{t}
            </Badge>
          ))}
        </div>

        <div className="mt-auto space-y-3">
          {/* RSVP Block para eventos futuros */}
          {!past && (
            <RSVPBlock 
              event={ev} 
              onShowReminder={() => {
                setShowReminderModal(true);
                pushGA('reminder_open', { event_id: ev.id });
              }} 
            />
          )}
          
          <div className="grid grid-cols-2 gap-3">
          {!past ? (
            <>
              {ev.join_url && (
                <a 
                  href={ev.join_url} 
                  target="_blank" 
                  rel="noreferrer" 
                  onClick={() => pushGA('click_join', { event_id: ev.id })} 
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-darling-lilac/30 bg-darling-lilac text-white hover:bg-deep-purple dark:border-darling-lilac dark:bg-darling-lilac dark:hover:bg-deep-purple px-4 py-2.5 text-sm font-exo font-semibold transition-colors shadow-sm"
                >
                  <Video className="h-4 w-4"/> Entrar
                </a>
              )}
              <a 
                href={toGoogleCalendarUrl(ev)} 
                target="_blank" 
                rel="noreferrer" 
                onClick={() => pushGA('add_to_calendar', { event_id: ev.id })} 
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-light-gray-alt dark:border-deep-purple/50 bg-light-gray dark:bg-deep-purple/30 hover:bg-light-gray-alt dark:hover:bg-deep-purple/50 px-4 py-2.5 text-sm font-exo font-medium text-soft-black dark:text-white transition-colors"
              >
                <Calendar className="h-4 w-4"/> Google Agenda
              </a>
              <button 
                onClick={handleICS} 
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-light-gray-alt dark:border-deep-purple/50 bg-light-gray dark:bg-deep-purple/30 hover:bg-light-gray-alt dark:hover:bg-deep-purple/50 px-4 py-2.5 text-sm font-exo font-medium text-soft-black dark:text-white transition-colors"
              >
                <Download className="h-4 w-4"/> Arquivo .ics
              </button>
              {ev.rsvp_url && (
                <a 
                  href={ev.rsvp_url} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-light-gray-alt dark:border-deep-purple/50 bg-light-gray dark:bg-deep-purple/30 hover:bg-light-gray-alt dark:hover:bg-deep-purple/50 px-4 py-2.5 text-sm font-exo font-medium text-soft-black dark:text-white transition-colors"
                >
                  <ExternalLink className="h-4 w-4"/> Inscrição
                </a>
              )}
            </>
          ) : (
            <>
              {ev.recording_url ? (
                <a 
                  href={ev.recording_url} 
                  target="_blank" 
                  rel="noreferrer" 
                  onClick={() => pushGA('click_recording', { event_id: ev.id })} 
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-ocean-water/30 bg-ocean-water text-white hover:bg-teal-green dark:border-ocean-water dark:bg-ocean-water dark:hover:bg-teal-green px-4 py-2.5 text-sm font-exo font-semibold transition-colors shadow-sm"
                >
                  <Video className="h-4 w-4"/> Gravação
                </a>
              ) : (
                <span className="col-span-2 text-sm text-darling-lilac/60 dark:text-darling-lilac/70 text-center py-2 font-exo">
                  Sem link de gravação informado.
                </span>
              )}
              {ev.join_url && (
                <a 
                  href={ev.join_url} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-light-gray-alt dark:border-deep-purple/50 bg-light-gray dark:bg-deep-purple/30 hover:bg-light-gray-alt dark:hover:bg-deep-purple/50 px-4 py-2.5 text-sm font-exo font-medium text-soft-black dark:text-white transition-colors"
                >
                  <LinkIcon className="h-4 w-4"/> Página do evento
                </a>
              )}
            </>
          )}
        </div>
        </div>

        {/* Compartilhar */}
        <div className="flex items-center gap-3 text-xs text-darling-lilac/70 dark:text-darling-lilac/90 pt-2 border-t border-light-gray-alt dark:border-deep-purple/30 font-exo">
          <Share2 className="h-3.5 w-3.5"/>
          <a className="hover:text-ocean-water transition-colors" target="_blank" rel="noreferrer" href={share.whatsapp}>
            WhatsApp
          </a>
          <span>·</span>
          <a className="hover:text-blue-sky transition-colors" target="_blank" rel="noreferrer" href={share.twitter}>
            Twitter
          </a>
          <span>·</span>
          <a className="hover:text-classic-blue transition-colors" target="_blank" rel="noreferrer" href={share.linkedin}>
            LinkedIn
          </a>
        </div>

        {ev.status && ev.status !== "scheduled" && (
          <div className="text-xs text-sun-flower dark:text-sun-flower bg-sun-flower/10 dark:bg-sun-flower/20 px-3 py-1.5 rounded-lg font-exo font-medium">
            Status: {ev.status === "cancelled" ? "Cancelado" : ev.status === "postponed" ? "Adiado" : ev.status}
          </div>
        )}
      </div>
      
      {/* Reminder Modal */}
      <ReminderModal 
        isOpen={showReminderModal}
        onClose={() => setShowReminderModal(false)}
        event={ev}
      />
    </motion.article>
  );
}